import React from 'react';

import { withCurrentUser } from '../../hocs/with-current-user';

export type VisibleProperties = {
  taskList: {
    debtor: boolean;
    job: boolean;
    task: boolean;
    client: boolean;
    assigned: boolean;
    status: boolean;
    dueDate: boolean;
    estimatedTime: boolean;
  };
  taskBoard: {
    debtor: boolean;
    job: boolean;
    task: boolean;
    client: boolean;
    assigned: boolean;
    dueDate: boolean;
    estimatedTime: boolean;
  };
  jobList: {
    debtor: boolean;
    job: boolean;
    assignee: boolean;
    partner: boolean;
    manager: boolean;
    staff: boolean;
    status: boolean;
    dueDate: boolean;
    budget: boolean;
    tasks: boolean;
  };
  jobBoard: {
    debtor: boolean;
    job: boolean;
    assignee: boolean;
    partner: boolean;
    manager: boolean;
    staff: boolean;
    dueDate: boolean;
    tasks: boolean;
  };
  completedJobTable: {
    assignee: boolean;
    partner: boolean;
    manager: boolean;
    staff: boolean;
    job: boolean;
    debtor: boolean;
    startDate: boolean;
    dueDate: boolean;
    completedDate: boolean;
    turnaround: boolean;
    delivery: boolean;
  };
};

// the order of these properties for the List style ones is important - it controls the default order of the columns in the list
export const defaultVisibleProperties: VisibleProperties = {
  taskList: {
    debtor: true,
    job: true,
    task: true,
    client: true,
    assigned: true,
    status: true,
    dueDate: true,
    estimatedTime: true,
  },
  taskBoard: {
    debtor: true,
    job: true,
    task: true,
    client: true,
    assigned: true,
    dueDate: true,
    estimatedTime: true,
  },
  jobList: {
    debtor: true,
    job: true,
    assignee: true,
    partner: true,
    manager: true,
    staff: true,
    status: true,
    dueDate: true,
    budget: true,
    tasks: true,
  },
  jobBoard: {
    debtor: true,
    job: true,
    assignee: true,
    partner: true,
    manager: true,
    staff: true,
    dueDate: true,
    tasks: true,
  },
  completedJobTable: {
    assignee: true,
    partner: true,
    manager: true,
    staff: true,
    job: true,
    debtor: true,
    startDate: true,
    dueDate: true,
    completedDate: true,
    turnaround: true,
    delivery: true,
  },
};

const VisiblePropertiesContext = React.createContext<
  [VisibleProperties, (s: React.SetStateAction<VisibleProperties>) => void]
>([defaultVisibleProperties, () => {}]);
VisiblePropertiesContext.displayName = 'VisiblePropertiesContext';

export const useVisibleProperties = () =>
  React.useContext(VisiblePropertiesContext);

export const VisiblePropertiesProvider = withCurrentUser(
  ({ currentUser, children }) => {
    const [visibleProperties, setVisibleProperties] =
      React.useState<VisibleProperties>(() => {
        const userId = currentUser.cloudId;
        const s = userId && localStorage.getItem(`visibleproperties|${userId}`);

        let result = s
          ? (JSON.parse(s) as VisibleProperties)
          : defaultVisibleProperties;

        // You might be tempted to spread so that we get the properties in the correct order, and any defaults
        // then, lay whatever came out of the parsed value (or the default if we've fallen back to that) over the top
        // EXCEPT, this zots over any custom ordering of the properties. This is BAD, so we cannot do this :(
        // const finalResult: VisibleProperties = {
        //   taskList: { ...defaultVisibleProperties.taskList, ...result.taskList },
        //   etc etc
        // };

        if (!result.completedJobTable) {
          result.completedJobTable = {
            ...defaultVisibleProperties.completedJobTable,
          };
        }
        if (!result.jobBoard.hasOwnProperty('assignee')) {
          // unlike the list visible properties, there is no "order" to these properties for the kanban boards visible properties
          // so we can just set the default here wherever we need
          result.jobBoard.assignee = defaultVisibleProperties.jobBoard.assignee;
        }

        if (!result.jobList.hasOwnProperty('assignee')) {
          // The order of these properties matters, it's used to control the customised order of the columns
          // So, we do some dancing to try and insert the assignee column into the place where it should be
          // The alternatives were:
          // 1. Just add it into the end of the list, which is what a straight result.foo.bar = true does, they can sort it themselves. Blergh
          // 2. As above, add it to the end of the list, but leave it off. This is even worse than above, completely invisible property
          // 3. Reset the entirety of their job list and completed jobs table properties. This would be BRUTAL, but it happens when you turn something on anyway, so maybe it's not so bad?.
          // get the properties of the result
          const jobListEntries = Object.entries(result.jobList);
          // work out where Partner is. We want Assignee to be to the left of partner
          const indexOfPartner = jobListEntries.findIndex(
            ([key]) => key === 'partner'
          );
          // put the new property in where partner is currently. If we didn't find partner, put it at the start, that way it'll be visible
          jobListEntries.splice(indexOfPartner > -1 ? indexOfPartner : 0, 0, [
            'assignee',
            true,
          ]);
          // and update the result with a new object. We need to do the casting as typescript reckons Object.fromEntries isn't correct
          result.jobList = Object.fromEntries(
            jobListEntries
          ) as VisibleProperties['jobList'];
        }
        if (!result.completedJobTable.hasOwnProperty('assignee')) {
          // as above, only we want this to be to the right of staff
          const completedJobsEntries = Object.entries(result.completedJobTable);
          // work out where Staff is. We want Assignee to be to the right of staff
          const indexOfStaff = completedJobsEntries.findIndex(
            ([key]) => key === 'staff'
          );
          // put the new property in after staff. If we didn't find staff, put it at the start, that way it'll be visible
          completedJobsEntries.splice(
            indexOfStaff > -1 ? indexOfStaff + 1 : 0,
            0,
            ['assignee', true]
          );
          // and update the result with a new object. We need to do the casting as typescript reckons Object.fromEntries isn't correct
          result.completedJobTable = Object.fromEntries(
            completedJobsEntries
          ) as VisibleProperties['completedJobTable'];
        }

        return result;
      });
    React.useEffect(() => {
      const userId = currentUser.cloudId;
      if (userId)
        localStorage.setItem(
          `visibleproperties|${userId}`,
          JSON.stringify(visibleProperties)
        );
    }, [currentUser, visibleProperties]);

    return (
      <VisiblePropertiesContext.Provider
        value={[visibleProperties, setVisibleProperties]}
      >
        {children}
      </VisiblePropertiesContext.Provider>
    );
  }
);

VisiblePropertiesProvider.displayName = 'VisiblePropertiesProvider';
